import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { MessagingMessage_Read } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef } from 'react';

import { asReadEvent } from '~hooks/useMessaging/messagingReadEvent';
import {
  applyReadCountsToCache,
  replaceMessageInCache,
  upsertMessageIntoCache,
} from '~hooks/useMessaging/messagingTranscriptCache';
import { MessagingMessage } from '~hooks/useMessaging/models';
import { writePatchedList } from '~hooks/useMessaging/reReadPatchedList';
import { reReadUnreadCountsSoon } from '~hooks/useMessaging/reReadUnreadCountsSoon';
import useMessagingChannelCachePatch from '~hooks/useMessaging/useMessagingChannelCachePatch';
import useMessagingChannelListBump from '~hooks/useMessaging/useMessagingChannelListBump';
import {
  MESSAGING_QUERY_KEY,
  messagingMessageReadersQueryKey,
  messagingTranscriptQueryKey,
} from '~queries/messagingQueryKeys';
import type { MessagingMessagesPage } from '~queries/useMessagingMessagesInfiniteQuery';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import {
  isRealTimeConnectionOpen,
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';

const MESSAGING_HANDLER_KEY = 'messaging-real-time-updates';

const MESSAGE_EVENTS = ['create', 'update', 'delete'] as const;

type MessagingMessageEvent = {
  action: (typeof MESSAGE_EVENTS)[number];
  data: { messaging_channel_id: string; message: MessagingMessage_Read };
};

/** The envelope is absent from the OpenAPI spec by design, so it is narrowed by hand. */
const asMessageEvent = (payload: unknown): MessagingMessageEvent | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;

  const { data, action } = payload as Partial<MessagingMessageEvent>;
  if (!MESSAGE_EVENTS.includes(action as MessagingMessageEvent['action']))
    return undefined;
  if (!data || typeof data.messaging_channel_id !== 'string' || !data.message) {
    return undefined;
  }

  return payload as MessagingMessageEvent;
};

type MessagingUnreadEvent = {
  action: 'unread';
  data: { messaging_channel_id: string; unread_count: number; muted: boolean };
};

const asUnreadEvent = (payload: unknown): MessagingUnreadEvent | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;

  const { data, action } = payload as Partial<MessagingUnreadEvent>;
  if (action !== 'unread') return undefined;
  if (
    !data ||
    typeof data.messaging_channel_id !== 'string' ||
    typeof data.unread_count !== 'number' ||
    typeof data.muted !== 'boolean'
  ) {
    return undefined;
  }

  return payload as MessagingUnreadEvent;
};

type MessagingArchivedEvent = {
  action: 'archived';
  data: { messaging_channel_id: string; archived_at: string };
};

const asArchivedEvent = (payload: unknown): MessagingArchivedEvent | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;

  const { data, action } = payload as Partial<MessagingArchivedEvent>;
  if (action !== 'archived') return undefined;
  if (
    !data ||
    typeof data.messaging_channel_id !== 'string' ||
    typeof data.archived_at !== 'string'
  ) {
    return undefined;
  }

  return payload as MessagingArchivedEvent;
};

/** Subscribes on the viewer's own company, so cross-company conversations deliver. */
export default function useMessagingRealTimeUpdates({
  companyId,
  onMessageCreated,
}: {
  companyId: Nullable<string>;
  onMessageCreated?: (channelId: string, message: MessagingMessage) => void;
}) {
  const queryClient = useQueryClient();
  const { messagingConnectionStore } = useStores();
  const { bumpChannelInLists, replaceChannelPreviewInLists } =
    useMessagingChannelListBump();
  const { patchChannelInCaches } = useMessagingChannelCachePatch();
  const onMessageCreatedRef = useRef(onMessageCreated);
  onMessageCreatedRef.current = onMessageCreated;

  const applyEvent = useCallback(
    (payload: unknown) => {
      const unreadEvent = asUnreadEvent(payload);
      if (unreadEvent) {
        const { messaging_channel_id, unread_count, muted } = unreadEvent.data;

        patchChannelInCaches(messaging_channel_id, (channel) =>
          channel.withReadState({ unreadCount: unread_count, muted }),
        );
        reReadUnreadCountsSoon(queryClient);
        return;
      }

      const archivedEvent = asArchivedEvent(payload);
      if (archivedEvent) {
        const { messaging_channel_id, archived_at } = archivedEvent.data;

        patchChannelInCaches(
          messaging_channel_id,
          (channel) => channel.withArchivedAt(dayjs(archived_at)),
          { shouldReReadListsLacking: true },
        );
        return;
      }

      const readEvent = asReadEvent(payload);
      if (readEvent) {
        const { messaging_channel_id, read_counts } = readEvent.data;

        writePatchedList<InfiniteData<MessagingMessagesPage>>(
          queryClient,
          messagingTranscriptQueryKey(messaging_channel_id),
          (existing) => applyReadCountsToCache(existing, read_counts),
        );
        read_counts.forEach(({ message_id }) => {
          void queryClient.resetQueries({
            queryKey: messagingMessageReadersQueryKey(messaging_channel_id, message_id),
          });
        });
        return;
      }

      const messageEvent = asMessageEvent(payload);
      if (!messageEvent) return;

      const channelId = messageEvent.data.messaging_channel_id;
      const message = MessagingMessage.parse(messageEvent.data.message);
      const transcriptKey = messagingTranscriptQueryKey(channelId);

      // Seeding a transcript from one event would look complete with no history.
      writePatchedList<InfiniteData<MessagingMessagesPage>>(
        queryClient,
        transcriptKey,
        (existing) =>
          messageEvent.action === 'create'
            ? existing && upsertMessageIntoCache(existing, message)
            : replaceMessageInCache(existing, message),
      );

      if (messageEvent.action === 'create') {
        bumpChannelInLists(channelId, message, { shouldReReadListsLacking: true });
        onMessageCreatedRef.current?.(channelId, message);
      } else {
        replaceChannelPreviewInLists(channelId, message);
      }
    },
    [bumpChannelInLists, patchChannelInCaches, queryClient, replaceChannelPreviewInLists],
  );

  useEffect(() => {
    if (!companyId) {
      messagingConnectionStore.markConnected();
      return;
    }

    subscribeWithMultipleHandlers(
      realTimeChannels.MessagingUserChannel,
      companyId,
      MESSAGING_HANDLER_KEY,
      applyEvent,
      undefined,
      // Every connect re-reads — the first one has the same gap as a reconnect.
      () => {
        messagingConnectionStore.markConnected();
        void queryClient.invalidateQueries({ queryKey: [MESSAGING_QUERY_KEY] });
      },
      () => messagingConnectionStore.markDisconnected(),
    );

    // ActionCable sends no `disconnected` to a subscription created while the socket is down.
    if (!isRealTimeConnectionOpen()) {
      messagingConnectionStore.markDisconnected();
    }

    return () => {
      unsubscribeHandler(
        realTimeChannels.MessagingUserChannel,
        companyId,
        MESSAGING_HANDLER_KEY,
      );
    };
  }, [applyEvent, companyId, messagingConnectionStore, queryClient]);

  useEffect(
    () => () => messagingConnectionStore.markConnected(),
    [messagingConnectionStore],
  );
}
